import { Archive } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button, Callout, ConfirmDialog, Spinner, TextField } from "@/components/ui";
import { deleteTarget, fetchImpact, setArchived } from "@/data/spaces";
import { ApiError } from "@/lib/api";
import { toast } from "@/lib/toast";

/** What a lifecycle action applies to. `name` is the display name; for a product `spaceKey` is where to go after a delete. */
export type LifecycleTarget = { kind: "space" | "product"; key: string; name: string; spaceKey?: string };

/** Archives or unarchives and tells the user. Unarchive needs no confirmation: nothing is lost. */
export async function toggleArchived(target: LifecycleTarget, archived: boolean) {
  try {
    await setArchived(target, archived);
  } catch (e) {
    toast.show({ title: e instanceof ApiError && e.isNetwork ? m.error_network() : m.error_generic(), tone: "danger" });
    if (archived) throw e;
    return;
  }
  toast.show(
    {
      title: archived
        ? m.lifecycle_archived_toast({ name: target.name })
        : m.lifecycle_unarchived_toast({ name: target.name }),
      tone: "success",
    },
    { timeout: 3500 },
  );
}

/** Confirmation for archiving a product or a space (reversible, so a plain confirm). */
export function ArchiveDialog({
  target,
  isOpen,
  onOpenChange,
}: {
  target: LifecycleTarget;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const space = target.kind === "space";
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={
        space
          ? m.lifecycle_archive_space_title({ name: target.name })
          : m.lifecycle_archive_product_title({ name: target.name })
      }
      description={space ? m.lifecycle_archive_space_body() : m.lifecycle_archive_product_body()}
      confirmLabel={m.lifecycle_archive_confirm()}
      tone="primary"
      onConfirm={() => toggleArchived(target, true)}
    />
  );
}

/**
 * Delete confirmation. An empty product or space is a plain confirm. One that still holds items (trash
 * included) lists what goes with it and asks for its key to be typed before "Delete permanently" enables:
 * that path sends `?purge=1`.
 */
export function DeleteDialog({
  target,
  isOpen,
  onOpenChange,
}: {
  target: LifecycleTarget;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [typed, setTyped] = useState("");
  const impact = useQuery({
    queryKey: ["impact", target.kind, target.key],
    queryFn: () => fetchImpact(target),
    enabled: isOpen,
    staleTime: 0,
    gcTime: 0,
  });
  const data = impact.data;
  const purge = !!data && data.items + data.trashed > 0;
  const space = target.kind === "space";
  const ready = !!data && (!purge || typed.trim().toUpperCase() === target.key);

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onOpenChange={(o) => {
        if (!o) setTyped("");
        onOpenChange(o);
      }}
      title={m.lifecycle_delete_title({ name: target.name })}
      description={
        !data
          ? m.common_loading()
          : purge
            ? m.lifecycle_delete_purge_intro({ name: target.name })
            : space
              ? m.lifecycle_delete_empty_space({ name: target.name })
              : m.lifecycle_delete_empty_product({ name: target.name })
      }
      confirmLabel={purge ? m.lifecycle_delete_purge_confirm() : m.lifecycle_delete_confirm()}
      confirmDisabled={!ready}
      describeError={(e) =>
        e instanceof ApiError && e.isConflict
          ? m.lifecycle_delete_not_empty()
          : e instanceof ApiError && e.isNetwork
            ? m.error_network()
            : m.error_generic()
      }
      onConfirm={async () => {
        await deleteTarget(target, purge);
        toast.show({ title: m.lifecycle_deleted_toast({ name: target.name }), tone: "success" }, { timeout: 3500 });
        if (space) void navigate({ to: "/" });
        else if (target.spaceKey) void navigate({ to: "/s/$spaceKey", params: { spaceKey: target.spaceKey } });
        else void navigate({ to: "/" });
      }}
    >
      {impact.isPending ? (
        <Spinner />
      ) : impact.isError ? null : data && purge ? (
        <>
          <ul className="flex flex-col gap-0.5 rounded-control border border-line px-3 py-2 text-sm text-fg-muted tnum">
            <li>{m.lifecycle_impact_items({ count: data.items })}</li>
            <li>{m.lifecycle_impact_trashed({ count: data.trashed })}</li>
            <li>{m.lifecycle_impact_comments({ count: data.comments })}</li>
            {space ? (
              <li>{m.lifecycle_impact_products({ products: data.products.map((p) => p.key).join(", ") })}</li>
            ) : null}
          </ul>
          <TextField
            label={m.lifecycle_delete_type_key({ key: target.key })}
            value={typed}
            onChange={setTyped}
            autoComplete="off"
          />
        </>
      ) : null}
    </ConfirmDialog>
  );
}

/**
 * Notice at the top of an archived space or product. Admins get the Unarchive button (for a product inside an
 * archived space the space is what to unarchive, so the button belongs to the space then).
 */
export function ArchivedBanner({
  kind,
  canUnarchive,
  onUnarchive,
}: {
  kind: "space" | "product";
  canUnarchive: boolean;
  onUnarchive: () => Promise<unknown>;
}) {
  const [pending, setPending] = useState(false);
  return (
    <Callout
      tone="warning"
      role="status"
      title={m.lifecycle_banner_title()}
      className="items-center"
      actions={
        canUnarchive ? (
          <Button
            size="sm"
            variant="secondary"
            isPending={pending}
            onPress={() => {
              setPending(true);
              void onUnarchive().finally(() => setPending(false));
            }}
          >
            <Archive size={14} aria-hidden="true" />
            {m.lifecycle_unarchive()}
          </Button>
        ) : undefined
      }
    >
      {kind === "space" ? m.lifecycle_banner_space() : m.lifecycle_banner_product()}
    </Callout>
  );
}
